/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Truck, Plus, Search, Trash2, Loader2, Mail, Sparkles, Upload, Check, Pencil,
  Building2, Users, Download, ArrowRightLeft,
} from 'lucide-react';

const API = '/api';

const empty = () => ({
  company_name: '',
  email: '',
  country: '',
  notes: '',
  category: 'regional_distributor',
});

export default function RegionalDistributorsPage() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const bulkDocxInputRef = useRef(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkLastRun, setBulkLastRun] = useState(null);
  const [deepEnriching, setDeepEnriching] = useState(false);
  const [deepEnrichProgress, setDeepEnrichProgress] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [partnersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/company-profiles?category=regional_distributor`, { withCredentials: true }),
        axios.get(`${API}/pharma-contacts?limit=100000`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);
      setItems(partnersRes.data || []);
      const cdata = contactsRes.data;
      setContacts(cdata.items || cdata || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const contactCountFor = (companyName) => {
    if (!companyName) return 0;
    const key = companyName.trim().toLowerCase();
    return contacts.filter(c => (c.company_name || '').trim().toLowerCase() === key).length;
  };

  const emailsFor = (companyName) => {
    if (!companyName) return [];
    const key = companyName.trim().toLowerCase();
    const list = [];
    // 1. Org-level emails from the company_profile itself (display_email, email, partnering_email)
    const profile = items.find(p => (p.company_name || '').trim().toLowerCase() === key);
    if (profile) {
      const orgEmails = [profile.display_email, profile.email, profile.partnering_email]
        .map(e => (e || '').trim())
        .filter(Boolean);
      orgEmails.forEach(em => list.push({ email: em, name: '' }));
    }
    // 2. Per-person emails from pharma_contacts attached to this company
    contacts
      .filter(c => (c.company_name || '').trim().toLowerCase() === key)
      .forEach(c => {
        const em = (c.email || '').trim();
        if (em) list.push({ email: em, name: (c.name || c.full_name || '').trim() });
      });
    // Dedupe by email (case-insensitive)
    const seen = new Set();
    return list.filter(x => {
      const k = x.email.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  const filtered = items.filter(p => {
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (p.company_name || '').toLowerCase().includes(s) ||
           (p.email || '').toLowerCase().includes(s) ||
           (p.display_email || '').toLowerCase().includes(s) ||
           (p.country || '').toLowerCase().includes(s) ||
           (p.notes || '').toLowerCase().includes(s) ||
           (p.therapeutic_interests || []).some(a => (a || '').toLowerCase().includes(s));
  });

  const handleBulkDocxChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(docx|pdf|pptx)$/i.test(file.name)) {
      toast.error('Please upload a .docx, .pdf, or .pptx file');
      e.target.value = '';
      return;
    }
    setBulkRunning(true);
    setBulkLastRun(null);
    const fd = new FormData();
    fd.append('file', file);
    const toastId = toast.loading(`Uploading ${file.name}...`);
    try {
      const { data: kickoff } = await axios.post(`${API}/imports/bulk-distributor-docx`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
      });
      const jobId = kickoff.job_id;
      toast.message('Parsing with Claude…', { id: toastId, description: '60–120s for ~20 partner profiles.' });
      const start = Date.now();
      let final = null;
      while (Date.now() - start < 15 * 60_000) {
        await new Promise(r => setTimeout(r, 3000));
        let job;
        try {
          const { data } = await axios.get(`${API}/imports/bulk-distributor-docx/${jobId}`, { withCredentials: true });
          job = data;
        } catch { continue; }
        if (job.stage === 'matching' || job.stage === 'parsing') {
          const p = job.progress || 0;
          const s = job.summary || {};
          const desc = job.stage === 'parsing'
            ? (job.parse_chunks_total
                ? `LLM parsing chunk ${job.parse_chunks_done || 0}/${job.parse_chunks_total} · ${job.parse_companies_found || 0} partners found`
                : 'LLM working…')
            : `${s.enriched_existing || 0} enriched · ${s.created_new || 0} new so far…`;
          toast.message(`${job.stage === 'parsing' ? 'Parsing' : 'Enriching'} — ${p}%`, { id: toastId, description: desc });
        } else if (job.stage === 'done') { final = job; break; }
        else if (job.stage === 'failed') { throw new Error(job.error || 'Enrichment failed'); }
      }
      if (!final) throw new Error('Enrichment timed out after 15 minutes');
      const s = final.summary || {};
      setBulkLastRun({ enriched: s.enriched_existing || 0, created: s.created_new || 0 });
      toast.success(`Done — ${s.enriched_existing || 0} enriched, ${s.created_new || 0} new partners, ${s.contacts_added || 0} contacts`, { id: toastId });
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Bulk partner import failed', { id: toastId });
    } finally {
      setBulkRunning(false);
      if (bulkDocxInputRef.current) bulkDocxInputRef.current.value = '';
    }
  };

  const submitCreate = async (e) => {
    e?.preventDefault?.();
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/company-profiles/${editing.id}`, {
          ...editing,
          company_name: form.company_name.trim(),
          email: form.email.trim().toLowerCase(),
          country: form.country.trim(),
          notes: form.notes.trim(),
          category: 'regional_distributor',
        }, { withCredentials: true });
        toast.success('Partner updated');
      } else {
        await axios.post(`${API}/company-profiles`, {
          company_name: form.company_name.trim(),
          email: form.email.trim().toLowerCase(),
          country: form.country.trim(),
          notes: form.notes.trim(),
          category: 'regional_distributor',
          company_type: 'Mid-pharma',
          therapeutic_interests: [],
          disease_areas: {},
        }, { withCredentials: true });
        toast.success('Partner added');
      }
      setForm(empty());
      setEditing(null);
      setShowCreate(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const moveToPharma = async (item) => {
    if (!window.confirm(`Move "${item.company_name}" to Pharma?`)) return;
    try {
      await axios.put(`${API}/company-profiles/${item.id}`, {
        ...item,
        category: 'pharma',
      }, { withCredentials: true });
      toast.success('Moved to Pharma');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Move failed');
    }
  };

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API}/company-profiles/${id}`, { withCredentials: true });
      toast.success('Removed');
      setDeleteId(null);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleExportCSV = async () => {
    try {
      const res = await axios.get(`${API}/company-profiles/export-csv?category=regional_distributor`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'commercialization_partners.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export');
    }
  };

  const handleDeepEnrichAll = async () => {
    if (!window.confirm(
      'Deep Enrich All Commercialization Partners?\n\n' +
      'Runs Perplexity research on every distributor profile missing country footprint ' +
      `(${items.length} partner${items.length !== 1 ? 's' : ''} in DB). Pulls country footprint, ` +
      'therapeutic franchises, distribution channels (hospital / retail / specialty / OTC / tender), ' +
      'in-licensing appetite, and recent deals (last 24 months).\n\n' +
      'Idempotent — already-enriched partners are skipped. Safe to re-run.'
    )) return;
    setDeepEnriching(true);
    setDeepEnrichProgress(null);
    try {
      const res = await axios.post(
        `${API}/company-profiles/deep-research-all-distributors`,
        { only_missing: true },
        { withCredentials: true },
      );
      if (res.data?.status?.running) {
        toast.info('Distributor enrichment already in progress');
      } else {
        toast.success('Distributor enrichment started — polling progress…');
      }
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await axios.get(
            `${API}/company-profiles-deep-research-distributors/status`,
            { withCredentials: true },
          );
          const status = statusRes.data;
          setDeepEnrichProgress(status);
          if (!status.running) {
            clearInterval(pollInterval);
            setDeepEnriching(false);
            setDeepEnrichProgress(null);
            toast.success(
              `Distributor enrichment complete — enriched ${status.updated} of ${status.total} ` +
              `partner${status.total !== 1 ? 's' : ''}${status.errors ? ` (${status.errors} errors)` : ''}.`,
              { duration: 8000 },
            );
            await fetchItems();
          }
        } catch {
          clearInterval(pollInterval);
          setDeepEnriching(false);
          setDeepEnrichProgress(null);
        }
      }, 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start distributor enrichment');
      setDeepEnriching(false);
    }
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      company_name: p.company_name || '',
      email: p.display_email || p.email || '',
      country: p.country || '',
      notes: p.notes || '',
      category: 'regional_distributor',
    });
    setShowCreate(true);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5" data-testid="regional-distributors-page">
      {/* Header — mirrors Pharma Companies */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Truck className="w-6 h-6 text-[#00709B]" />
            Commercial Partners
          </h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} partner{items.length !== 1 ? 's' : ''} in database</p>
          <p className="text-sm text-slate-500 mt-1">Regional commercialization &amp; in-licensing partners (Tamer Group, Medison, Zai Lab, etc.)</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={handleExportCSV} data-testid="rd-export-csv-btn" className="text-sm">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={handleDeepEnrichAll}
            disabled={deepEnriching}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm"
            data-testid="rd-deep-enrich-all-btn"
            title="Run Perplexity deep-research on every distributor missing country footprint. Pulls country list, therapeutic franchises, channels (hospital / retail / specialty / OTC / tender), in-licensing appetite & recent deals. Idempotent — safe to re-run; only fills missing profiles."
          >
            {deepEnriching ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {deepEnrichProgress
                  ? `Enriching ${deepEnrichProgress.completed}/${deepEnrichProgress.total}${deepEnrichProgress.current_company ? ` · ${deepEnrichProgress.current_company.slice(0, 18)}…` : ''}`
                  : 'Starting…'}
              </>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2 text-emerald-600" /> Enrich All (MOAT)</>
            )}
          </Button>
          <input
            ref={bulkDocxInputRef}
            type="file" accept=".docx,.pdf,.pptx" className="hidden"
            onChange={handleBulkDocxChange}
            disabled={bulkRunning}
            data-testid="rd-bulk-docx-input"
          />
          <Button
            variant="outline"
            className="border-violet-300 text-violet-700 hover:bg-violet-50"
            onClick={() => bulkDocxInputRef.current?.click()}
            disabled={bulkRunning}
            data-testid="rd-import-conference-btn"
            title="Upload a conference brochure (.docx, .pdf, or .pptx) — Claude parses partner profiles and enriches your CRM"
          >
            {bulkRunning ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enriching…</>
            ) : bulkLastRun ? (
              <><Check className="w-4 h-4 mr-2" /> Done · {bulkLastRun.enriched}+{bulkLastRun.created} · Upload next</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Import Conference File</>
            )}
          </Button>
          <Link to="/imports">
            <Button
              variant="outline"
              data-testid="rd-import-csv-btn"
              className="text-sm"
              title="Bulk-import biotech, pharma, partners and investors via CSV/Excel with auto-classification"
            >
              <Upload className="w-4 h-4 mr-2" /> Import CSV
            </Button>
          </Link>
          <Button
            onClick={() => { setEditing(null); setForm(empty()); setShowCreate(true); }}
            className="bg-[#00709B] hover:bg-[#005a7a] text-white"
            data-testid="rd-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Partner
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by company, country, region, therapeutic area…"
          className="pl-9"
          data-testid="rd-search-input"
        />
      </div>

      {/* Table — mirrors Pharma Companies columns */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="py-16 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">
              {items.length === 0
                ? <>No commercialization partners yet. Click <span className="font-semibold text-slate-600">Add Partner</span> or <span className="font-semibold text-slate-600">Import Conference File</span>.</>
                : 'No results matching your search'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <Table className="w-full" data-testid="rd-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[22%]">Company</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[10%]">Type</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[8%]">Contacts</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[28%]">Therapeutic Interests</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[18%]">Contact Emails</TableHead>
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 w-[14%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(p => {
                const tas = Array.isArray(p.therapeutic_interests) ? p.therapeutic_interests : [];
                const MAX_TA = 4;
                const visibleTas = tas.slice(0, MAX_TA);
                const hiddenTas = tas.length - MAX_TA;
                const emails = emailsFor(p.company_name);
                const count = contactCountFor(p.company_name);
                return (
                  <TableRow key={p.id} className="hover:bg-slate-50/50" data-testid={`rd-row-${p.id}`}>
                    <TableCell>
                      <Link
                        to={`/company-profile/${p.id}`}
                        className="text-sm font-semibold text-slate-900 hover:text-[#00709B] hover:underline"
                        data-testid={`rd-link-${p.id}`}
                      >
                        {p.company_name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px] font-normal">
                        {p.company_type || 'Partner'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {count > 0 ? (
                        <div className="flex items-center gap-1.5 text-xs text-slate-700">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          <span className="font-medium">{count}</span>
                        </div>
                      ) : <span className="text-xs text-slate-400">0</span>}
                    </TableCell>
                    <TableCell className="align-top">
                      {tas.length === 0 ? (
                        <span className="text-xs text-slate-400">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1" title={tas.join(' · ')}>
                          {visibleTas.map((a, i) => (
                            <Badge key={i} variant="secondary" className="text-[10px] font-normal max-w-[160px] truncate inline-block" title={a}>
                              {a}
                            </Badge>
                          ))}
                          {hiddenTas > 0 && (
                            <Badge variant="outline" className="text-[10px] font-normal text-slate-500">+{hiddenTas} more</Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      {emails.length === 0 ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-rose-500 font-medium" title="No contact emails found for this distributor — add one from the company profile">⚠ No email</span>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-0.5" title={emails.map(e => `${e.name}: ${e.email}`).join('\n')}>
                          {emails.slice(0, 2).map((e, i) => (
                            <a key={i} href={`mailto:${e.email}`} className="text-[11px] text-slate-700 hover:text-sky-700 hover:underline truncate inline-flex items-center gap-1" data-testid={`rd-email-${p.id}-${i}`}>
                              <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                              <span className="truncate">{e.email}</span>
                            </a>
                          ))}
                          {emails.length > 2 && (
                            <span className="text-[10px] text-slate-500 italic">+{emails.length - 2} more</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Button
                          variant="ghost" size="sm"
                          className="text-slate-500 hover:text-slate-800 h-8 w-8 p-0"
                          onClick={() => moveToPharma(p)}
                          title="Move this record into Pharma instead"
                          data-testid={`rd-move-pharma-${p.id}`}
                        >
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          className="text-slate-500 hover:text-slate-800 h-8 w-8 p-0"
                          onClick={() => openEdit(p)}
                          title="Edit"
                          data-testid={`rd-edit-${p.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          onClick={() => setDeleteId(p.id)}
                          title="Delete"
                          data-testid={`rd-delete-${p.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) { setEditing(null); setForm(empty()); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Commercial Partner' : 'Add Commercial Partner'}</DialogTitle>
            <DialogDescription>
              Non-pharma in-licensers, commercialization partners, or licensing aggregators.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Company name *</label>
              <Input
                value={form.company_name}
                onChange={e => setForm({ ...form, company_name: e.target.value })}
                placeholder="e.g. Tamer Group, Lunatus"
                required
                data-testid="rd-form-name"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Primary email</label>
              <Input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="bd@tamergroup.com"
                data-testid="rd-form-email"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Country / Region</label>
              <Input
                value={form.country}
                onChange={e => setForm({ ...form, country: e.target.value })}
                placeholder="e.g. Saudi Arabia (MENA)"
                data-testid="rd-form-country"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Notes</label>
              <Textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Focus areas, deal preferences, regional rights specialties…"
                rows={3}
                data-testid="rd-form-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => { setShowCreate(false); setEditing(null); setForm(empty()); }}>Cancel</Button>
              <Button
                type="submit"
                className="bg-[#00709B] hover:bg-[#005a7a] text-white"
                disabled={saving}
                data-testid="rd-form-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : editing ? <Check className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                {editing ? 'Save changes' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this partner?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the company record and all linked contacts.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteItem(deleteId)}
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
